import type { Placement } from '@popperjs/core';
import { easings, useTransition } from '@react-spring/web';
import { useCallback, useRef, useState, type CSSProperties, type PropsWithChildren } from 'react';
import { usePopper } from 'react-popper';
import { useMixpanel } from '../../contexts';
import { useCallbackRef } from '../../hooks';
import { Z_INDEX } from '../../styles';
import { MixpanelEvent, MixpanelEventProperty } from '../../tokens';
import { PopoverContent } from '../Popover/PopoverContent';
import { PopoverVariant } from '../Popover/types';
import { TourContext } from './TourContext';
import { TourStep } from './TourStep';
import type { ITourProviderProps, PersistedTourState } from './types';

const TRANSITION = {
  overflow: 'hidden',
  from: { opacity: 0 as CSSProperties['opacity'], transform: 'translateY(8px)' },
  enter: { opacity: 1 as CSSProperties['opacity'], transform: 'translateY(0)' },
  leave: { opacity: 0 as CSSProperties['opacity'], transform: 'translateY(8px)' },
  config: {
    duration: 300,
    easing: easings.easeOutBack,
  },
  unique: true,
} satisfies Parameters<typeof useTransition>[1];

const DEFAULT_OFFSET: [number, number] = [0, 0];
const DEFAULT_PLACEMENT: Placement = 'auto';

function getVisibleTourAnchorElement(anchor: string): Element | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const element = document.querySelector(anchor);
  if (element == null) {
    console.warn(`Could not find element with anchor ${anchor}`);
    return null;
  }

  const style = element.ownerDocument.defaultView?.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  if (style?.display === 'none' || style?.visibility === 'hidden' || rect.width === 0 || rect.height === 0) {
    console.warn(`Could not find visible element with anchor ${anchor}`);
    return null;
  }

  return element;
}

/**
 * Compares two version strings (e.g., "2.55", "2.55.1").
 * Returns: negative if a < b, positive if a > b, 0 if equal.
 */
function compareVersions(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

/**
 * The TourProvider contains all the logic for when to show/hide tours.
 *
 * The list of `tours` must be provided up front so we know in which order to render steps of a tour.
 * Persistence is handled externally via `persistedState` and `onStateChange` props.
 *
 * @param children
 * @param tours List of tours to render
 * @param persistedState Current persisted state from external storage
 * @param onStateChange Callback to persist state changes
 * @param firstSeenVersion User's first seen version for filtering tours
 * @param enable Small hack to disable the tour provider when running tests
 */
export function TourProvider({
  children,
  tours,
  persistedState,
  onStateChange,
  firstSeenVersion,
  enable = import.meta.env.VITE_IS_TEST_ENVIRONMENT !== 'true',
}: PropsWithChildren<ITourProviderProps>) {
  const [currentTour, setCurrentTour] = useState<string | undefined>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [currentOffset, setCurrentOffset] = useState<[number, number]>(DEFAULT_OFFSET);
  const [currentPlacement, setCurrentPlacement] = useState<Placement>(DEFAULT_PLACEMENT);

  const tourRef = useRef<string | undefined>(undefined);
  const queueRef = useRef<string[]>([]);

  const [targetRef, setTargetRef] = useState<Element | null>();
  const [contentRef, setContentRef] = useState<HTMLElement | null>();

  const mixpanel = useMixpanel();

  const popper = usePopper(targetRef, contentRef, {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          padding: 0,
        },
      },
      {
        name: 'offset',
        options: {
          offset: currentOffset,
        },
      },
    ],
    placement: currentPlacement,
  });

  // Declare simple utility functions first (no dependencies on other callbacks)
  const pollQueue = useCallback(() => {
    if (queueRef.current.length === 0) {
      return;
    }
    const head = queueRef.current[0];
    queueRef.current = queueRef.current.slice(1);
    return head;
  }, []);

  const clearQueue = useCallback(() => {
    queueRef.current = [];
  }, []);

  const getTour = useCallbackRef((tourName: string) => tours[tourName]);

  /**
   * Render the popover at the given anchor.
   */
  const render = useCallback((anchor: string) => {
    setTargetRef(getVisibleTourAnchorElement(anchor));
  }, []);

  const updatePersistedState = useCallbackRef((newState: PersistedTourState): void => {
    onStateChange(newState);
  });

  /**
   * Determines if a tour should be skipped because the user joined after the feature was released.
   * Users who join after a feature exists don't need a tour explaining it.
   */
  const shouldSkipTourForNewUser = useCallbackRef((tourName: string): boolean => {
    // If firstSeenVersion is null, user existed before we started tracking - they should see all tours
    if (firstSeenVersion == null) {
      return false;
    }

    const tour = tours[tourName];
    if (tour == null) {
      return false;
    }

    // User is "new" for this tour if their first version >= tour release version
    return compareVersions(firstSeenVersion, tour.releasedInVersion) >= 0;
  });

  /**
   * Starts the tour immediately, cancelling any other tour that is currently running.
   *
   * Note that if we've already seen this tour, it will not be run.
   */
  const startTour = useCallbackRef((tourName: string) => {
    const tour = getTour(tourName);
    if (tour == null) {
      console.warn(`Could not find tour with name ${tourName}`);
      const nextTour = pollQueue();
      if (nextTour != null) {
        startTour(nextTour);
      }
      return;
    }

    // Don't show tours to users who joined after this tour was released
    if (shouldSkipTourForNewUser(tourName)) {
      const nextTour = pollQueue();
      if (nextTour != null) {
        startTour(nextTour);
      }
      return;
    }

    const persistedStep = persistedState[tourName];
    if (tour.steps.length - 1 === persistedStep) {
      // Already ran this tour, run next one
      const nextTour = pollQueue();
      if (nextTour != null) {
        startTour(nextTour);
      }
      return;
    }

    const { offset, placement, anchor } = tour.steps[persistedStep ?? 0];
    const anchorElement = getVisibleTourAnchorElement(anchor);
    if (anchorElement == null) {
      const nextTour = pollQueue();
      if (nextTour != null) {
        startTour(nextTour);
      }
      return;
    }

    tourRef.current = tourName;
    setCurrentTour(tourName);
    setCurrentStep(persistedStep ?? 0);
    setCurrentOffset(offset ?? DEFAULT_OFFSET);
    setCurrentPlacement(placement ?? DEFAULT_PLACEMENT);
    setTargetRef(anchorElement);

    // Persist the current step
    updatePersistedState({ ...persistedState, [tourName]: persistedStep ?? 0 });
  });

  /**
   * Add a tour to the queue
   */
  const enqueueTour = useCallbackRef((tour: string) => {
    if (queueRef.current.length === 0 && tourRef.current == null) {
      startTour(tour);
      return;
    }
    queueRef.current = queueRef.current.includes(tour) ? queueRef.current : [...queueRef.current, tour];
  });

  /**
   * Remove a tour from the queue
   */
  const dequeueTour = useCallback((tour: string) => {
    queueRef.current = queueRef.current.filter(t => t !== tour);
  }, []);

  /**
   * End the current tour
   */
  const endTour = useCallbackRef((tour: string) => {
    if (tour !== tourRef.current) {
      return;
    }

    const currentTourDefinition = getTour(tour);
    if (currentTourDefinition == null) {
      setCurrentTour(undefined);
      tourRef.current = undefined;
      setCurrentStep(0);
      return;
    }

    const nextTour = pollQueue();
    if (nextTour != null) {
      startTour(nextTour);
      // startTour can skip (missing/hidden anchor, already seen, etc.) without taking over.
      // Only skip finalizing this tour when a successor actually became current.
      if (tourRef.current !== tour) {
        return;
      }
    }

    // Mark tour as completed (persisted step = last step)
    updatePersistedState({ ...persistedState, [tourRef.current]: currentTourDefinition.steps.length - 1 });

    setCurrentTour(undefined);
    tourRef.current = undefined;
    setCurrentStep(0);
  });

  /**
   * Navigate to a step in a tour.
   */
  const visitStep = useCallbackRef((step: number) => {
    if (tourRef.current == null) {
      throw new Error('Cannot visit step when no tour is active');
    }
    const tour = getTour(tourRef.current);
    if (tour == null) {
      throw new Error(`Cannot visit step for unknown tour ${tourRef.current}`);
    }
    if (step < 0) {
      throw new Error('Cannot visit step less than 0');
    }
    if (step >= tour.steps.length) {
      endTour(tourRef.current);
      return;
    }

    const { offset, placement, anchor } = tour.steps[step];

    setCurrentStep(step);
    setCurrentOffset(offset ?? DEFAULT_OFFSET);
    setCurrentPlacement(placement ?? DEFAULT_PLACEMENT);
    render(anchor);

    // Persist the current step
    updatePersistedState({ ...persistedState, [tourRef.current]: step });
  });

  const prevStep = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.PrevTourStep, {
      [MixpanelEventProperty.Tour]: currentTour,
    });
    visitStep(currentStep - 1);
  });

  const nextStep = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.NextTourStep, {
      [MixpanelEventProperty.Tour]: currentTour,
    });
    visitStep(currentStep + 1);
  });

  /**
   * End all tours and mark them as displayed.
   */
  const endAllTours = useCallbackRef(() => {
    setCurrentTour(undefined);
    tourRef.current = undefined;
    setCurrentStep(0);
    clearQueue();
    updatePersistedState(
      Object.keys(tours).reduce((acc, tour) => ({ ...acc, [tour]: tours[tour].steps.length - 1 }), {})
    );
  });

  const handleDismiss = useCallbackRef(() => {
    if (currentTour) {
      mixpanel.track(MixpanelEvent.DismissTour, {
        [MixpanelEventProperty.Tour]: currentTour,
      });
      endTour(currentTour);
    }
  });

  const handleDismissAll = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.DismissAllTours);
    endAllTours();
  });

  const transitions = useTransition(enable ? currentTour : false, TRANSITION);

  return (
    <TourContext.Provider
      value={{
        tours,
        startTour,
        endTour,

        enqueueTour,
        dequeueTour,

        nextStep,
        visitStep,
        prevStep,
      }}
    >
      {children}
      {transitions(
        (transitionStyle, item, t) =>
          item &&
          currentTour && (
            <PopoverContent
              variant={PopoverVariant.Primary}
              style={{ ...popper.styles.popper }}
              ref={setContentRef}
              key={t.key}
              placement={currentPlacement}
              transitionStyle={transitionStyle}
              isSmall={false}
              noPaddingAndBorder={false}
              zIndex={Z_INDEX.dropdown + 1}
              {...popper.attributes.popper}
            >
              <TourStep
                title={tours[currentTour].steps[currentStep]?.title}
                description={tours[currentTour].steps[currentStep]?.description}
                readMoreURL={tours[currentTour].steps[currentStep]?.readMoreURL}
                showNavigation={tours[currentTour].steps?.length > 1}
                onNext={() => nextStep()}
                onPrev={currentStep !== 0 ? () => prevStep() : undefined}
                onDismiss={handleDismiss}
                onDismissAll={handleDismissAll}
                showClose={true}
              />
            </PopoverContent>
          )
      )}
    </TourContext.Provider>
  );
}
